import React from 'react';
import { Github, Linkedin, Mail, ArrowUp, Instagram } from 'lucide-react';
import { personalInfo, navLinks } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.1)',
};

export default function Footer() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const year = new Date().getFullYear();

  return (
    <footer style={{
      position: 'relative', zIndex: 2,
      borderTop: `1px solid ${C.border}`,
      background: '#010306',
    }}>
      {/* Main footer */}
      <div style={{ padding: '52px 60px 32px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 40, marginBottom: 48,
        }}>
          {/* Brand */}
          <div>
            <div style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: 20, fontWeight: 900, letterSpacing: 2,
              marginBottom: 12,
            }}>
              <span style={{ color: C.cyan }}>MARSHALL</span>
              <span style={{ color: C.green }}>VD</span>
              <span style={{ color: C.cyan }}>.</span>
            </div>
            <p style={{
              fontSize: 13.5, color: C.gray, lineHeight: 1.75,
              maxWidth: 240, marginBottom: 20,
            }}>
              AI Engineer & Software Developer based in Bali, Indonesia. Building intelligent systems for the future.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { icon: Github, href: personalInfo.github },
                { icon: Linkedin, href: personalInfo.linkedin },
                { icon: Instagram, href: personalInfo.instagram },
                { icon: Mail, href: `mailto:${personalInfo.email}` },
              ].map(({ icon: Icon, href }, i) => (
                <a key={i} href={href} target="_blank" rel="noreferrer"
                  style={{
                    width: 36, height: 36,
                    background: 'rgba(0,245,255,0.04)',
                    border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.gray, transition: 'all .25s', textDecoration: 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = C.cyan;
                    e.currentTarget.style.color = C.cyan;
                    e.currentTarget.style.background = 'rgba(0,245,255,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.color = C.gray;
                    e.currentTarget.style.background = 'rgba(0,245,255,0.04)';
                  }}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <div style={{
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 3, textTransform: 'uppercase',
              color: C.cyan, marginBottom: 18,
            }}>
              Navigation
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {navLinks.map(link => (
                <a key={link.href} href={link.href}
                  onClick={e => { e.preventDefault(); document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' }); }}
                  style={{
                    fontSize: 13.5, color: C.gray, textDecoration: 'none',
                    transition: 'color .2s', letterSpacing: 0.5,
                  }}
                  onMouseEnter={e => e.target.style.color = C.cyan}
                  onMouseLeave={e => e.target.style.color = C.gray}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div>
            <div style={{
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 3, textTransform: 'uppercase',
              color: C.cyan, marginBottom: 18,
            }}>
              Expertise
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Machine Learning', 'LLM Evaluation', 'Web Development', 'Data Science', 'AI QA', 'UI/UX Design'].map((s, i) => (
                <span key={i} style={{ fontSize: 13.5, color: C.gray, letterSpacing: 0.5 }}>{s}</span>
              ))}
            </div>
          </div>

          {/* Status card */}
          <div>
            <div style={{
              fontFamily: "'Space Mono', monospace", fontSize: 9,
              letterSpacing: 3, textTransform: 'uppercase',
              color: C.cyan, marginBottom: 18,
            }}>
              Status
            </div>
            <div style={{
              background: 'rgba(0,255,136,0.04)',
              border: '1px solid rgba(0,255,136,0.18)',
              padding: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, animation: 'pulse 1.5s ease-in-out infinite' }} />
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: C.green, letterSpacing: 1.5 }}>
                  AVAILABLE
                </span>
              </div>
              <p style={{ fontSize: 12, color: C.gray, lineHeight: 1.7 }}>
                Open for full-time, freelance, and AI collaboration projects.
              </p>
              <a href={`mailto:${personalInfo.email}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginTop: 14,
                  fontFamily: "'Space Mono', monospace", fontSize: 9,
                  letterSpacing: 2, textTransform: 'uppercase',
                  color: C.cyan, textDecoration: 'none',
                  transition: 'opacity .2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <Mail size={11} /> Get In Touch →
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(0,245,255,0.15), transparent)',
          marginBottom: 24,
        }} />

        {/* Bottom bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 14,
        }}>
          <div style={{
            fontFamily: "'Space Mono', monospace", fontSize: 9,
            letterSpacing: 1.5, color: C.gray,
          }}>
            © {year} {personalInfo.name}. Crafted with ⚡ in Bali, Indonesia.
          </div>
          <div style={{
            fontFamily: "'Space Mono', monospace", fontSize: 9,
            letterSpacing: 2, color: C.gray,
            display: 'flex', gap: 20, flexWrap: 'wrap',
          }}>
            <span>REACT JS</span>
            <span style={{ color: 'rgba(0,245,255,0.3)' }}>—</span>
            <span>ORBITRON FONT</span>
            <span style={{ color: 'rgba(0,245,255,0.3)' }}>—</span>
            <span>SPACE MONO</span>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <button
        onClick={scrollTop}
        style={{
          position: 'absolute', bottom: 24, right: 60,
          width: 40, height: 40,
          background: 'rgba(0,245,255,0.06)',
          border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: C.gray,
          transition: 'all .25s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.cyan; e.currentTarget.style.color = C.cyan; e.currentTarget.style.background = 'rgba(0,245,255,0.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.gray; e.currentTarget.style.background = 'rgba(0,245,255,0.06)'; }}
        aria-label="Scroll to top"
      >
        <ArrowUp size={16} />
      </button>

      <style>{`
        @media (max-width: 768px) {
          footer > div { padding: 40px 24px 24px !important; }
        }
      `}</style>
    </footer>
  );
}
